<template>
	<validation-observer tag="div">
		<div class="connection-step">
			<!-- System Integration Selection -->
			<div class="step-section-card">
				<div class="step-section-text">
					<h3 class="step-section-title">
						Which integration system should this service use?
					</h3>
					<p class="step-section-description">
						Choose the integration system that this service is a part of. This determines where your data will be sent and how it will be authenticated.
					</p>
				</div>
				<div class="step-section-content">
					<Dropdown
						label="System *"
						:value="integrationId"
						:items="systemIntegrationItems"
						searchable
						rules="required"
						class="form-dropdown"
						:disabled="!userFullPermissions"
						@input="$emit('update:integrationId', $event)"
					/>
				</div>
			</div>

			<!-- HTTP Configuration -->
			<div class="step-section-card">
				<div class="step-section-text">
					<h3 class="step-section-title">
						How should this service connect to the API?
					</h3>
					<p class="step-section-description">
						Configure the HTTP method and endpoint URL that will be used to send data to your integration system.
					</p>
				</div>
				<div class="step-section-content">
					<div class="http-config-form">
						<div class="http-method-section">
							<Dropdown
								label="HTTP Method *"
								:value="httpMethod"
								:items="httpMethodItems"
								rules="required"
								class="form-dropdown"
								tooltip-text="Set the HTTP method being used to transmit data"
								:disabled="!userFullPermissions"
								@input="$emit('update:httpMethod', $event)"
							/>
						</div>

						<div class="endpoint-section">
							<TextField
								label="Endpoint Sub-Address *"
								:value="requestUrl"
								rules="required"
								class="form-dropdown"
								tooltip-text="Specify the API endpoint sub-address for this specific service. e.g. /services/data/Contact/"
								:disabled="!userFullPermissions"
								@input="$emit('update:requestUrl', $event)"
							/>
						</div>
					</div>
				</div>
			</div>

			<!-- Request Body Configuration -->
			<div class="step-section-card">
				<div class="step-section-text">
					<h3 class="step-section-title">
						What data should be sent in the request?
					</h3>
					<p class="step-section-description">
						Build the request body template that will be used for sending data to your integration system.
					</p>
				</div>
				<div class="step-section-content">
					<div class="json-editor-container">
						<JsonEditor
							:value="requestBody || ''"
							:disabled="!userFullPermissions"
							:placeholder="requestBodyPlaceholder"
							@input="$emit('update:requestBody', $event)"
						/>
					</div>
				</div>
			</div>

			<!-- Advanced Settings -->
			<div class="step-section-card">
				<div class="step-section-text">
					<h3 class="step-section-title">
						What are the advanced settings?
					</h3>
					<p class="step-section-description">
						Optional settings for request throttling to prevent overwhelming the target system.
					</p>
				</div>
				<div class="step-section-content">
					<div class="advanced-settings-grid">
						<div class="throttling-section">
							<TextField
								:value="requestThrottlingWaitTimeMS"
								label="Request Throttling (ms)"
								class="form-dropdown"
								type="number"
								placeholder="0"
								tooltip-text="Time delay in milliseconds between requests to prevent overwhelming the target system. Use 0 for no throttling."
								:disabled="!userFullPermissions"
								@input="$emit('update:requestThrottlingWaitTimeMS', $event)"
							/>
						</div>
					</div>
				</div>
			</div>

			<!-- Custom Headers -->
			<div class="step-section-card">
				<div class="step-section-text">
					<h3 class="step-section-title">
						Do you need custom headers?
					</h3>
					<p class="step-section-description">
						Add custom HTTP headers to be included with each request. This can be useful for API keys, content type specifications, or other service requirements.
					</p>
				</div>
				<div class="step-section-content">
					<div class="custom-headers-section">
						<div class="add-header-form">
							<div class="form-row">
								<TextField
									v-model="customHeaderName"
									class="form-dropdown"
									label="Header Name"
									placeholder="e.g. Authorization, Content-Type"
									tooltip-text="Input the name of the custom header to be sent as part of any outbound message to your integration system"
									:disabled="!userFullPermissions"
								/>
								<TextField
									v-model="customHeaderValue"
									class="form-dropdown"
									label="Header Value"
									placeholder="e.g. Bearer {token}, application/json"
									tooltip-text="Input the value to be sent within this custom header"
									:disabled="!userFullPermissions"
								/>
								<div class="button-group">
									<PrimaryActionButton
										v-if="userFullPermissions"
										:disabled="!customHeaderName || !customHeaderValue"
										@click="addCustomHeader"
									>
										Add Header
									</PrimaryActionButton>
								</div>
							</div>
						</div>

						<div
							v-if="customHeaders && customHeaders.length > 0"
							class="headers-table"
						>
							<v-simple-table
								dense
								light
							>
								<template #default>
									<thead>
										<tr>
											<th
												class="text-left"
												scope="col"
												style="width: 45%;"
											>
												Header Name
											</th>
											<th
												class="text-left"
												scope="col"
												style="width: 45%;"
											>
												Header Value
											</th>
											<th
												class="text-center"
												scope="col"
												style="width: 10%;"
											>
												Action
											</th>
										</tr>
									</thead>
									<tbody>
										<tr
											v-for="(header, index) in customHeaders"
											:key="index"
										>
											<td>
												{{ displayCustomHeaderName(index) }}
											</td>
											<td>
												{{ displayCustomHeaderValue(index) }}
											</td>
											<td class="text-center">
												<v-menu
													v-if="userFullPermissions"
													bottom
													left
												>
													<template #activator="{ on, attrs }">
														<v-btn
															icon
															v-bind="attrs"
															v-on="on"
														>
															<v-icon>
																mdi-dots-horizontal
															</v-icon>
														</v-btn>
													</template>
													<v-list dense>
														<v-list-item @click="removeCustomHeader(index)">
															<v-list-item-content>
																<v-list-item-title>
																	Remove Header
																</v-list-item-title>
															</v-list-item-content>
														</v-list-item>
													</v-list>
												</v-menu>
											</td>
										</tr>
									</tbody>
								</template>
							</v-simple-table>
						</div>

						<div
							v-else
							class="empty-state"
						>
							<p>
								No custom headers configured
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	</validation-observer>
</template>
<script>
import TextField from '../../../../../../shared/components/text-field.vue'
import Dropdown from '../../../../../../shared/components/dropdown.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import { JsonEditor } from '../../../../components/component-library-wrapper.js'

export default {
	components: { Dropdown, TextField, PrimaryActionButton, JsonEditor },
	props: {
		httpMethod: String,
		authenticationUrl: String,
		requestUrl: String,
		requestBody: {
			type: String,
			default () {
				return ''
			}
		},
		httpMethodItems: Array,
		userFullPermissions: Boolean,
		cassieObjects: Array,
		cassieObjectId: Number,
		requestThrottlingWaitTimeMS: {
			type: Number,
			default () {
				return 0
			}
		},
		customHeaders: {
			type: Array,
			default () {
				return []
			}
		}
	},
	data () {
		return {
			customHeaderName: null,
			customHeaderValue: null
		}
	},
	computed: {
		requestBodyPlaceholder () {
			return `{
	"Name": "{{Name}}",
	"Email": "{{Email}}"
}`
		},
		availableFields () {
			if (!this.cassieObjects || !this.cassieObjectId) {
				return []
			}
			const selectedObject = this.cassieObjects.find(obj => obj.id === this.cassieObjectId)
			if (!selectedObject || !selectedObject.fields) {
				return []
			}
			return selectedObject.fields
		},
		fieldsForPayloadBuilder () {
			return this.availableFields.map(field => {
				return {
					label: field.displayName || field.fieldName,
					value: `{{${field.fieldName}}}`,
					description: field.typeFriendlyName || 'Field'
				}
			})
		}
	},
	methods: {
		updateCustomHeaders (customHeaders) {
			this.$emit('update:customHeaders', customHeaders)
		},
		removeCustomHeader (index) {
			const customHeaders = [...this.customHeaders]
			this.$delete(customHeaders, index)
			this.updateCustomHeaders(customHeaders)
		},
		addCustomHeader () {
			this.updateCustomHeaders([
				...this.customHeaders,
				{
					headerName: this.customHeaderName,
					headerValue: this.customHeaderValue
				}
			])
			this.customHeaderName = null
			this.customHeaderValue = null
		},
		displayCustomHeaderName (index) {
			return this.customHeaders[index]?.headerName
		},
		displayCustomHeaderValue (index) {
			return this.customHeaders[index]?.headerValue
		}
	}
}
</script>

<!-- Import shared stepper styles -->
<style lang="scss" scoped>
@import './shared/stepper-styles.scss';

.connection-step {
	// Component-specific container styles
	padding: 0;
}

// HTTP Configuration Section - Enhanced spacing
.http-config-section {
	margin-bottom: 40px;

	.section-header {
		margin-bottom: 32px;
	}

	.http-config-form {
		display: grid;
		gap: 24px;
		grid-template-columns: 1fr 2fr;
		align-items: start;

		@media (max-width: 768px) {
			grid-template-columns: 1fr;
			gap: 20px;
		}
	}

	.http-method-section,
	.endpoint-section {
		display: flex;
		flex-direction: column;
	}
}

.form-row {
	display: flex;
	gap: 16px;
	align-items: flex-start;
	flex-wrap: wrap;
	margin-bottom: 24px;
}

// Shared styles are automatically applied via import
// No need for @extend since classes are used directly in template

.field-label-container {
	margin-bottom: 8px;
}

.field-label {
	font-size: 15px;
	font-weight: 600;
	color: #1e293b;
	margin-bottom: 4px;
	display: block;
}

.field-help-text {
	font-size: 14px;
	color: var(--global-muted-foreground);
	margin: 0;
	line-height: 1.5;
}

.request-body-section {
	margin-bottom: 24px;
}

.advanced-settings-grid {
	display: grid;
	gap: 16px;
	margin-bottom: 16px;
}

.throttling-section {
	max-width: 300px;
}

.custom-headers-section {
	margin-top: 16px;
}

.add-header-form {
	margin-bottom: 16px;
}

.headers-table {
	margin-top: 16px;
	border: 1px solid var(--global-border-color);
	border-radius: var(--radius, 8px);
	overflow: hidden;
}

.empty-state {
	padding: 24px;
	text-align: center;
	color: var(--global-muted-foreground);
	background-color: var(--global-main-background-color);
	border: 1px solid var(--global-border-color);
	border-radius: var(--radius, 8px);
	margin-top: 16px;

	p {
		margin: 0;
		font-size: 14px;
	}
}

.button-group {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	margin-left: 8px;
}

/* JsonEditor styling */
.request-body-section :deep(.code-editor-container) {
	font-size: 14px;
	border: 1px solid var(--global-border-color);
	border-radius: var(--radius, 8px);
}

.request-body-section :deep(.code-editor-header) {
	background-color: var(--global-main-background-color);
	border-bottom: 1px solid var(--global-border-color);
}

/* Validation error styling */
:deep(.v-text-field.error--text .v-label) {
	color: var(--global-color-orange-darken-3) !important; /* Amber color instead of red */
}

:deep(.v-text-field.error--text .v-input__control .v-input__slot fieldset) {
	border-color: var(--global-color-orange-darken-3) !important; /* Amber border for validation errors */
}

:deep(.v-text-field.error--text .v-input__control .v-input__slot .v-text-field__slot input) {
	color: var(--global-color-orange-darken-3) !important; /* Amber text for validation errors */
}
</style>
